import React, { useEffect } from 'react'
import { useInView } from 'react-intersection-observer'

import { BeatIcon } from '../beaticons'

import { useCopyStore, useEvent, useInsightsMicroWidgetStore } from '../context/hooks'

import { ON_CLICK_EVENT, ON_VIEW_EVENT, ON_VIEW_FIRST_TIME_EVENT } from '../constants/Analytics'
import { VIEW_THRESHOLD } from '../constants/App'

import type { BeatLikeOffer, MicroInsightCardProps } from '../interfaces'
import type { Beat } from '../../core/types/Beat'
import MicroCardTemplate from '../MicroCardTemplate'

export interface InsightCardProps extends MicroInsightCardProps {
  beat: Beat | BeatLikeOffer
  index?: number
}

export const InsightCard: React.FC<InsightCardProps> = ({
  ariaLabel,
  beat,
  elevation,
  index,
  isActiveCard,
  onCardClick,
  posthog,
  widthOverrides,
  variant,
  showIcon,
  heightOverrides,
}) => {
  const { ref, inView } = useInView({
    threshold: VIEW_THRESHOLD,
  })

  // *************** Context functions *********************
  const { cardsCopy } = useCopyStore()
  const { onEvent } = useEvent()
  const { updateBeat, updateOffer } = useInsightsMicroWidgetStore()

  // ************** Use Effects Hooks **********************
  useEffect(() => {
    if (inView) {
      if (!beat.has_been_displayed) {
        if (beat.guid.startsWith('OFR')) {
          updateOffer({
            ...beat,
            has_been_displayed: true,
          })
        } else {
          updateBeat({
            ...beat,
            has_been_displayed: true,
          })
        }
        onEvent(ON_VIEW_FIRST_TIME_EVENT(beat.template))
      } else {
        onEvent(ON_VIEW_EVENT(beat.template))
      }
    }
  }, [inView])

  useEffect(() => {
    if (inView && beat) {
      if (beat.template === 'MarketingOffer') {
        const offerBeat = beat as BeatLikeOffer
        posthog?.capture('viewed_messaging_insight', {
          template: beat.template,
          campaign_guid: offerBeat.campaign_guid,
          campaign_name: offerBeat.pulse_campaign_name,
          offer_guid: offerBeat.guid,
          user_guid: offerBeat.user_guid,
        })
      } else {
        posthog?.capture('viewed_insight', {
          beat_guid: beat.guid,
          template: beat.template,
          position: index,
        })
      }
    }
  }, [beat, index, inView])

  // *************** Click handlers ************************
  const handleClick = () => {
    /* eslint-disable no-unused-expressions */
    onCardClick && onCardClick(beat)
    onEvent(ON_CLICK_EVENT(beat.template))

    if (beat.template === 'MarketingOffer') {
      const offerBeat = beat as BeatLikeOffer
      posthog?.capture('clicked_messaging_insight', {
        template: beat.template,
        campaign_guid: offerBeat.campaign_guid,
        campaign_name: offerBeat.pulse_campaign_name,
        offer_guid: offerBeat.guid,
        user_guid: offerBeat.user_guid,
      })
    } else {
      posthog?.capture('clicked_insight', {
        beat_guid: beat.guid,
        template: beat.template,
        position: index,
      })
    }
  }

  return (
    <MicroCardTemplate
      aria-roledescription="slide"
      ariaLabel={ariaLabel}
      description={
        beat?.html_micro_description && beat?.html_micro_description.length > 0
          ? beat.html_micro_description
          : beat.html_description || ''
      }
      elevation={elevation}
      heightOverrides={heightOverrides}
      icon={showIcon && <BeatIcon beat={beat} />}
      isActiveCard={isActiveCard}
      onCardClick={handleClick}
      onPrimaryAction={handleClick}
      primaryActionText={beat.micro_call_to_action || cardsCopy?.micro_card_cta}
      ref={ref}
      title={beat.micro_title && beat.micro_title.length > 0 ? beat.micro_title : beat.title}
      variant={variant}
      widthOverrides={widthOverrides}
    />
  )
}
